import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Lock } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useLang } from "../context/LanguageContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { IMAGES } from "../lib/data";
import { toast } from "sonner";
import PaymentTrust from "../components/PaymentTrust";
import MondialRelayPicker from "../components/MondialRelayPicker";

const money = (n) => n.toFixed(2).replace(".", ",") + " €";

const volumeForItem = (item) => {
  const key = String(item?.key || "");
  if (key === "serum_duo_femme" || key === "serum_duo_homme" || key === "coffret_mixte") return "2 × 20 ml";
  if (key === "serum_femme" || key === "serum_homme" || key === "coffret_femme" || key === "coffret_homme") return "20 ml";
  return "";
};

export default function CartPage() {
  const { items, setQty, remove, clear, subtotal, discount, total, promo, setPromo,
          promoPercent, promoError, promoBusy, applyPromo, promoApplied } = useCart();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { universe } = useTheme();
  const cartUniverse = items[0]?.universe || universe;
  const { t, lang } = useLang();
  const [checkout, setCheckout] = useState(false);
  const [busy, setBusy] = useState(false);

  // Payment is account-only: if the client was redirected to login from the cart,
  // automatically reopen the checkout after successful authentication.
  useEffect(() => {
    if (user && location.state?.checkoutRequired) {
      setCheckout(true);
      navigate("/panier", { replace: true, state: {} });
    }
  }, [user, location.state, navigate]);

  const requireAccountForPayment = () => {
    if (user) return true;
    navigate("/connexion", {
      state: { from: "/panier", checkoutRequired: true },
    });
    return false;
  };
  const [shippingMethod, setShippingMethod] = useState("home");
  const [relay, setRelay] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", address: "", postal_code: "", city: "", country: "France" });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLookupBusy, setAddressLookupBusy] = useState(false);
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const merchandiseSubtotal = Math.max(0, subtotal - discount);
  const shippingFee = merchandiseSubtotal >= 60 ? 0 : (shippingMethod === "relay" ? 4.90 : 5.90);
  const checkoutTotal = +(merchandiseSubtotal + shippingFee).toFixed(2);

  // Address autocomplete uses the current IGN Géoplateforme service.
  // The former api-adresse.data.gouv.fr endpoint was retired in January 2026.
  // Debouncing also avoids firing a request for every single keystroke.
  const lookupAddress = async (value) => {
    setAddressConfirmed(false);
    const query = value.trim();
    if (!query || query.length < 3 || form.country !== "France") {
      setAddressSuggestions([]);
      setAddressLookupBusy(false);
      return;
    }

    setAddressLookupBusy(true);
    try {
      const params = new URLSearchParams({
        text: query,
        type: "StreetAddress",
        maximumResponses: "8",
      });

      // A postcode/city already entered helps narrow the suggestions.
      if (/^\d{5}$/.test(form.postal_code?.trim() || "")) {
        params.set("zipcode", form.postal_code.trim());
      }

      const response = await fetch(
        `https://data.geopf.fr/geocodage/completion/?${params.toString()}`
      );
      if (!response.ok) throw new Error(`Address completion failed: ${response.status}`);

      const data = await response.json();
      const results = Array.isArray(data.results) ? data.results : [];

      // Normalize the completion API response to the same shape used by the UI.
      const suggestions = results
        .filter((r) => r && (r.country === "StreetAddress" || r.zipcode || r.street))
        .map((r, index) => {
          const houseNumber = r.housenumber || r.houseNumber || r.number || "";
          const street = r.street || "";
          const streetWithNumber = [houseNumber, street].filter(Boolean).join(" ").trim();
          const label = r.fulltext || [streetWithNumber || r.street, r.zipcode, r.city].filter(Boolean).join(" ");
          return {
            id: `${streetWithNumber || r.fulltext || "address"}-${r.zipcode || ""}-${index}`,
            properties: {
              label,
              name: streetWithNumber || r.fulltext || "",
              postcode: r.zipcode || "",
              city: r.city || "",
              housenumber: houseNumber,
              street,
              type: "housenumber",
            },
          };
        });

      setAddressSuggestions(suggestions);
    } catch (e) {
      // Address lookup is an assistive service; manual entry remains available.
      setAddressSuggestions([]);
    } finally {
      setAddressLookupBusy(false);
    }
  };

  // Debounce address lookups so the public geocoding service is not called
  // once per character typed.
  useEffect(() => {
    const value = form.address;
    if (!value || value.trim().length < 3 || form.country !== "France") {
      setAddressSuggestions([]);
      setAddressLookupBusy(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      lookupAddress(value);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [form.address, form.postal_code, form.country]);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 pt-40 pb-32 text-center min-h-[70vh]">
        <h1 className="font-display text-5xl mb-6" style={{ fontWeight: 300 }}>{t("cart.empty_title")}</h1>
        <p className="text-sm mb-10" style={{ color: "var(--lo-muted)" }}>{t("cart.empty_desc")}</p>
        <Link to="/serum" className="btn-primary inline-flex items-center h-14 px-10" data-testid="empty-cart-cta">{t("common.discover_serum")}</Link>
      </div>
    );
  }


  const selectSuggestedAddress = (feature) => {
    const p = feature?.properties || {};
    setForm((prev) => {
      // Preserve the house number typed by the customer if the geocoder
      // returns only the street name.
      const typedHouseNumber = (prev.address || "").trim().match(/^(\d+[A-Za-z]?(?:\s*(?:bis|ter|quater))?)(?:\s+|$)/i)?.[1] || "";
      const suggestedName = (p.name || p.label || "").trim();
      const suggestedHasNumber = /^\d+[A-Za-z]?(?:\s*(?:bis|ter|quater))?\b/i.test(suggestedName);
      const houseNumber = p.housenumber || typedHouseNumber;
      const address = suggestedHasNumber || !houseNumber
        ? suggestedName || prev.address
        : `${houseNumber} ${suggestedName}`.trim();

      return {
        ...prev,
        address,
        postal_code: p.postcode || prev.postal_code,
        city: p.city || prev.city,
        country: "France",
      };
    });
    setAddressSuggestions([]);
    setAddressConfirmed(true);
  };

  const confirmManualAddress = () => {
    if (!form.address || !form.postal_code || !form.city || !form.country) {
      toast.error("Veuillez compléter votre adresse.");
      return;
    }
    setAddressConfirmed(true);
    toast.success("Adresse confirmée. Vous pouvez continuer vers le paiement.");
  };

  // With Point Relais / Locker, the confirmed relay is the delivery destination.
  // Keep the customer's own address in the billing form below.
  const deliveryAddress = shippingMethod === "relay" && relay
    ? {
        name: relay.name || "Point Relais / Locker",
        address: relay.address || "",
        postal_code: relay.postal_code || "",
        city: relay.city || "",
        country: relay.country === "FR" ? "France" : (relay.country || "France"),
      }
    : {
        name: "",
        address: form.address,
        postal_code: form.postal_code,
        city: form.city,
        country: form.country,
      };

  const validateShipping = () => {
    if (!addressConfirmed) {
      toast.error("Veuillez confirmer votre adresse de livraison.");
      return false;
    }
    if (shippingMethod === "relay" && !relay?.id) {
      toast.error("Veuillez sélectionner un Point Relais / Locker.");
      return false;
    }
    return true;
  };

  const payPal = async () => {
    if (!requireAccountForPayment()) return;
    if (!validateShipping()) return;
    if (!form.email || !form.address || !form.city || !form.postal_code || !form.first_name || !form.last_name) {
      toast.error("Veuillez compléter vos informations de livraison.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/paypal/create", {
        items: items.map((x) => ({ lookup_key: x.key, quantity: x.qty })),
        origin_url: window.location.origin, user_id: user ? user.id : null, promo_code: promoApplied || null, universe: cartUniverse,
        shipping_method: shippingMethod,
        relay_id: relay?.id || null,
        relay_name: relay?.name || "",
        relay_address: relay?.address || "",
        relay_postal_code: relay?.postal_code || "",
        relay_city: relay?.city || "",
        ...form, lang,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  const pay = async (e) => {
    e.preventDefault();
    if (!requireAccountForPayment()) return;
    if (!validateShipping()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((x) => ({ lookup_key: x.key, quantity: x.qty })),
        origin_url: window.location.origin,
        user_id: user ? user.id : null,
        promo_code: promoApplied || null,
        universe: cartUniverse,
        shipping_method: shippingMethod,
        relay_id: relay?.id || null,
        relay_name: relay?.name || "",
        relay_address: relay?.address || "",
        relay_postal_code: relay?.postal_code || "",
        relay_city: relay?.city || "",
      });
      await api.post("/orders", {
        session_id: data.session_id, ...form,
        full_name: `${form.first_name} ${form.last_name}`.trim(),
        items: items.map((x) => ({ label: x.label, quantity: x.qty })),
        shipping_method: shippingMethod,
        shipping_amount: shippingFee,
        relay_id: relay?.id || "",
        relay_name: relay?.name || "",
        relay_address: relay?.address || "",
        relay_postal_code: relay?.postal_code || "",
        relay_city: relay?.city || "",
        lang,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 pt-32 pb-32 min-h-[70vh]">
      <h1 className="font-display text-5xl mb-12" style={{ fontWeight: 300 }}>{checkout ? t("cart.title_order") : t("cart.title")}</h1>
      <div className="grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2 space-y-4">
          {items.map((it) => (
            <div key={it.key} className="card-lux p-6 flex gap-5" data-testid="cart-item">
              <img src={it.image || IMAGES.heroBottle} alt={it.label} loading="lazy" decoding="async" className="w-24 h-28 object-cover" style={{ border: "1px solid var(--lo-border)" }} />
              <div className="flex-1">
                <p className="overline mb-1">LUXE ORYVEA PREMIUM</p>
                <h3 className="font-display text-2xl mb-1" style={{ fontWeight: 400 }}>{it.label}</h3>
                {volumeForItem(it) && !/\b20\s*ml\b/i.test(it.label || "") && (
                  <p className="text-sm mb-3" style={{ color: "var(--lo-muted)" }}>{volumeForItem(it)}</p>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center border" style={{ borderColor: "var(--lo-border-rose)" }}>
                    <button className="w-9 h-9 flex items-center justify-center" onClick={() => setQty(it.key, it.qty - 1)} data-testid="cart-qty-minus"><Minus size={14} /></button>
                    <span className="w-9 text-center" data-testid="cart-qty">{it.qty}</span>
                    <button className="w-9 h-9 flex items-center justify-center" onClick={() => setQty(it.key, it.qty + 1)} data-testid="cart-qty-plus"><Plus size={14} /></button>
                  </div>
                  <span className="font-display text-2xl" style={{ color: "var(--lo-rose-hi)" }}>{money(it.unit * it.qty)}</span>
                </div>
              </div>
              <button onClick={() => remove(it.key)} className="self-start" data-testid="cart-remove"><Trash2 size={18} className="text-white/50 hover:text-[var(--lo-rose)] transition-colors" /></button>
            </div>
          ))}

          {!checkout && (
            <div className="card-lux p-6">
              <p className="overline mb-3">{t("cart.promo")}</p>
              <div className="flex gap-2">
                <input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder={t("cart.promo_placeholder")} className="flex-1 bg-transparent border-b py-2 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }} data-testid="promo-input" />
                <button onClick={applyPromo} disabled={promoBusy} className="btn-ghost h-10 px-5 disabled:opacity-50" data-testid="promo-apply">{promoBusy ? "…" : t("cart.apply")}</button>
              </div>
              {promoPercent > 0 && <p className="text-xs mt-2" style={{ color: "var(--lo-rose-hi)" }} data-testid="promo-success">{t("cart.applied")} : -{promoPercent}%</p>}
              {promoError && <p className="text-xs mt-2" style={{ color: "#e06666" }} data-testid="promo-error">{promoError}</p>}
            </div>
          )}

          {checkout && (
            <>
              <div className="card-lux p-6 mb-4 address-validation-card" data-testid="address-validation">
                <p className="overline mb-3">Adresse de livraison</p>

                {shippingMethod === "relay" && relay?.id ? (
                  <div className="space-y-3">
                    <p className="text-xs" style={{ color: "var(--lo-muted)" }}>
                      Votre Point Relais / Locker confirmé est automatiquement utilisé comme destination de livraison.
                    </p>
                    <div className="relay-picker-selected" data-testid="confirmed-delivery-address">
                      <strong>{deliveryAddress.name}</strong><br />
                      {deliveryAddress.address}<br />
                      {deliveryAddress.postal_code} {deliveryAddress.city}
                    </div>
                    <div className="text-xs" style={{ color: "var(--lo-rose-hi)" }}>
                      ✓ Adresse de livraison confirmée
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-xs mb-4" style={{ color: "var(--lo-muted)" }}>
                      Commencez à saisir votre adresse. Les suggestions françaises proviennent de la Base Adresse Nationale.
                      Si votre adresse est récente ou introuvable, vous pouvez la saisir manuellement.
                    </p>
                    <div className="space-y-4">
                      <input
                        value={form.address}
                        onChange={(e) => {
                          setForm({ ...form, address: e.target.value });
                        }}
                        placeholder="Adresse"
                        className="w-full bg-transparent border-b py-3 text-sm outline-none"
                        style={{ borderColor: "var(--lo-border)" }}
                        autoComplete="street-address"
                        data-testid="shipping-address"
                      />
                      {addressLookupBusy && <p className="text-xs" style={{ color: "var(--lo-muted)" }}>Recherche de l'adresse…</p>}
                      {addressSuggestions.length > 0 && (
                        <div className="address-suggestions" role="listbox">
                          {addressSuggestions.map((feature, index) => (
                            <button
                              type="button"
                              key={feature.id || feature.properties?.id || index}
                              onClick={() => selectSuggestedAddress(feature)}
                              className="address-suggestion"
                            >
                              {feature.properties?.label || feature.properties?.name}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          value={form.postal_code}
                          onChange={(e) => { setForm({ ...form, postal_code: e.target.value }); setAddressConfirmed(false); }}
                          placeholder="Code postal"
                          className="w-full bg-transparent border-b py-3 text-sm outline-none"
                          style={{ borderColor: "var(--lo-border)" }}
                          autoComplete="postal-code"
                        />
                        <input
                          value={form.city}
                          onChange={(e) => { setForm({ ...form, city: e.target.value }); setAddressConfirmed(false); }}
                          placeholder="Ville"
                          className="w-full bg-transparent border-b py-3 text-sm outline-none"
                          style={{ borderColor: "var(--lo-border)" }}
                          autoComplete="address-level2"
                        />
                      </div>
                      <input
                        value={form.country}
                        onChange={(e) => { setForm({ ...form, country: e.target.value }); setAddressConfirmed(false); }}
                        placeholder="Pays"
                        className="w-full bg-transparent border-b py-3 text-sm outline-none"
                        style={{ borderColor: "var(--lo-border)" }}
                        autoComplete="country-name"
                      />
                      <button
                        type="button"
                        onClick={confirmManualAddress}
                        className="btn-ghost h-12 w-full"
                        data-testid="confirm-address-btn"
                      >
                        {addressConfirmed ? "✓ Adresse confirmée" : "Confirmer cette adresse"}
                      </button>
                      {addressConfirmed && (
                        <div className="text-xs" style={{ color: "var(--lo-rose-hi)" }}>
                          Adresse confirmée : {form.address}, {form.postal_code} {form.city}, {form.country}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              {!user && (
                <div className="card-lux p-5 mb-4" role="alert">
                  <p className="font-display text-xl mb-1">Compte requis avant paiement</p>
                  <p className="text-sm" style={{ color: "var(--lo-muted)" }}>
                    Connectez-vous ou créez votre compte LUXE ORYVEA pour continuer vers le paiement sécurisé.
                  </p>
                </div>
              )}
              <div className="card-lux p-6 mb-4" data-testid="shipping-options">
                <p className="overline mb-4">Mode de livraison</p>
                <p className="text-xs mb-4" style={{ color: "var(--lo-muted)" }}>Livraison offerte dès 60 € d'achat.</p>
                <div className="space-y-3">
                  {[
                    ["home", "Livraison à domicile", "5,90 €", "2 à 4 jours ouvrés"],
                    ["relay", "Point Relais / Locker", "4,90 €", "2 à 4 jours ouvrés"],
                  ].map(([value, label, price, delay]) => {
                    const free = merchandiseSubtotal >= 60;
                    return (
                      <label key={value} className={`shipping-option ${shippingMethod === value ? "shipping-option--active" : ""}`}>
                        <input type="radio" name="shipping_method" value={value} checked={shippingMethod === value} onChange={() => {
                          setShippingMethod(value);
                          if (value === "home") {
                            setRelay(null);
                            setAddressConfirmed(false);
                          } else if (relay?.id) {
                            setAddressConfirmed(true);
                          }
                        }} />
                        <span className="shipping-option__copy"><strong>{label}</strong><small>{delay}</small></span>
                        <span className="shipping-option__price">{free ? "OFFERT" : price}</span>
                      </label>
                    );
                  })}
                </div>
                {shippingMethod === "relay" && (
                  <MondialRelayPicker
                    postalCode={form.postal_code}
                    onSelect={(selected) => {
                      setRelay(selected);
                      setAddressConfirmed(Boolean(selected?.id));
                    }}
                  />
                )}
              </div>
              <form id="checkout-form" onSubmit={pay} className="card-lux p-6">
              <p className="overline mb-4">{shippingMethod === "relay" ? "Adresse de facturation" : t("cart.billing")}</p>
              <div className="grid grid-cols-2 gap-4">
                {[["first_name", "field.first_name", "text", false], ["last_name", "field.last_name", "text", false],
                  ["email", "field.email", "email", true], ["phone", "field.phone", "tel", true],
                  ["address", "field.address", "text", true], ["postal_code", "field.postal_code", "text", false],
                  ["city", "field.city", "text", false], ["country", "field.country", "text", false]].map(([k, labelKey, type, full]) => (
                  <div key={k} className={full ? "col-span-2" : ""}>
                    <label className="overline block mb-1" style={{ fontSize: "0.55rem" }}>{t(labelKey)}</label>
                    <input required type={type} placeholder={t(labelKey)} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-transparent border-b py-2.5 text-sm outline-none" style={{ borderColor: "var(--lo-border)" }} data-testid={`checkout-${k}`} />
                  </div>
                ))}
              </div>
            </form>
            </>
          )}
        </div>

        <div className="card-lux p-8 h-fit" data-testid="cart-summary">
          <p className="overline mb-6">{t("cart.summary")}</p>
          <div className="space-y-3 text-sm mb-6" style={{ color: "var(--lo-muted)" }}>
            <div className="flex justify-between"><span>{t("cart.subtotal")}</span><span style={{ color: "var(--lo-text)" }}>{money(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between"><span>{t("cart.discount")}</span><span style={{ color: "var(--lo-rose-hi)" }}>-{money(discount)}</span></div>}
            <div className="flex justify-between"><span>{t("cart.shipping")}</span><span style={{ color: "var(--lo-rose-hi)" }}>{checkout ? (shippingFee === 0 ? "Offerte" : money(shippingFee)) : (merchandiseSubtotal >= 60 ? "Offerte" : "Dès 4,90 €")}</span></div>
          </div>
          <div className="gold-line mb-6" />
          <div className="flex justify-between items-end mb-8">
            <span className="overline">{t("cart.total")}</span>
            <span className="font-display text-3xl rose-gold-text" data-testid="cart-total">{money(checkout ? checkoutTotal : total)}</span>
          </div>
          {!checkout ? (
            <button className="btn-primary h-14 w-full" onClick={() => (user ? setCheckout(true) : requireAccountForPayment())} data-testid="proceed-checkout-btn">{t("cart.proceed")}</button>
          ) : (
            <>
              <button
                form="checkout-form"
                type="submit"
                disabled={busy}
                className="checkout-payment-art-btn disabled:opacity-50"
                data-testid="place-order-btn"
                aria-label={busy ? t("cart.redirect") : "Payer par carte"}
              >
                <img
                  src="/assets/payment-card-button-v6.png"
                  alt="Payer par carte — Visa, Mastercard, Apple Pay et Google Pay"
                  className="checkout-payment-art-btn__image"
                  draggable="false"
                />
                <span className="sr-only">{busy ? t("cart.redirect") : "Payer par carte"}</span>
              </button>
              <button
                type="button"
                onClick={payPal}
                disabled={busy}
                className="checkout-payment-art-btn checkout-payment-art-btn--paypal disabled:opacity-50"
                data-testid="paypal-btn"
                aria-label="Payer avec PayPal"
              >
                <img
                  src="/assets/payment-paypal-button-v6.png"
                  alt="Payer avec PayPal — Paiement sécurisé PayPal"
                  className="checkout-payment-art-btn__image"
                  draggable="false"
                />
                <span className="sr-only">Payer avec PayPal</span>
              </button>
              <div className="mt-5">
                <PaymentTrust compact />
              </div>
            </>
          )}
          <p className="text-[11px] text-center mt-4 flex items-center justify-center gap-1.5" style={{ color: "var(--lo-muted)" }}><Lock size={11} /> Paiement sécurisé. Apple Pay et Google Pay apparaissent lorsqu’ils sont disponibles sur votre appareil.</p>
        </div>
      </div>
    </div>
  );
}
